<template>
  <ModuleBase gray>
    <IntentReminderModal />
    <TwUltimatumModal />
    <div
      :class="[
        $style.base,
        {
          [$style.singleColumn]: singleColumn,
          [$style.isSmall]: $screen === 's'
        }
      ]"
    >
      <div :class="$style.inner">
        <div :class="$style.content">
          <div :class="$style.main">
            <TreatwellDac7Verification />
            <BaseAlert
              v-if="showSalonizedPayAlert"
              :text="$t('dashboard.salonized_pay_alert', brandNamePay())"
              color="warning"
              :primaryAction="{
                text: $t('global.actions.continue'),
                routerLink: {
                  name: 'salonized-pay'
                }
              }"
              v-test="'sp-alert'"
            />
            <QuickStart v-if="singleColumn" />
            <ScheduleActivity />
            <SalonizedPayVerification />
            <TreatwellMarketplaceBanner />
          </div>
          <div :class="$style.side">
            <BaseSpinner v-if="$apollo.loading" />
            <template v-else-if="dashboard">
              <div
                v-if="showSplitWalletsBanner"
                :class="$style.splitWalletsBanner"
              >
                <BaseVisualBanner
                  :heading="$t('dashboard.split_wallets_banner.heading')"
                  :description="$t('dashboard.split_wallets_banner.description')"
                  imagePath="/img/salonized-pay/onboarding/payout.svg"
                  :primaryAction="{
                    text: $t('dashboard.split_wallets_banner.primary_action'),
                    routerLink: {
                      name: 'salonized-pay'
                    }
                  }"
                  color="green"
                  v-test="'split-wallets-banner'"
                />
              </div>
              <BaseVisualBanner
                v-if="showTreatwellOfferBanner"
                :heading="
                  $t(
                    `dashboard.treatwell_offer.${isTrial ? 'group_1' : 'group_3'}.description`
                  )
                "
                :primaryAction="{
                  text: $t(
                    `dashboard.treatwell_offer.${isTrial ? 'group_1' : 'group_3'}.link`
                  ),
                  routerLink: {
                    name: !isTrial ? 'treatwell-landing' : 'subscription'
                  }
                }"
                size="s"
                :imagePath="
                  isTrial
                    ? '/img/treatwell/monitor.svg'
                    : '/img/store/usps/usp_1.svg'
                "
                mb
                v-test="'treatwell-offer-banner'"
              />
              <OnlineBookings v-if="isTrial" />
              <BoostBanner
                v-if="!showTreatwellOfferBanner"
                rounded
              />
              <BaseVisualBanner
                v-if="unleash.isEnabled('SalonizedPay') && (unleash.isEnabled('BlackFridayOffer') || unleash.isEnabled('FreeTerminalOffer')) && !hasTerminals"
                :heading="unleash.isEnabled('FreeTerminalOffer') ? $t('dashboard.black_friday_offer.heading_100_offer', { offerPrice }) : $t('dashboard.black_friday_offer.heading')"
                :description="unleash.isEnabled('FreeTerminalOffer') ? $t('dashboard.black_friday_offer.description_100_offer', { ...brandNamePay(), offerPrice }) : $t('dashboard.black_friday_offer.description', brandNamePay())"
                :imagePath="`/img/salonized-pay/getting-paid${isUk ? '-gb' : ''}${unleash.isEnabled('FreeTerminalOffer') ? '-100.png' : '.svg'}`"
                :primaryAction="{
                  text: unleash.isEnabled('FreeTerminalOffer') ? $t('global.actions.accept_offer') : $t('global.actions.claim_offer'),
                  routerLink: {
                    name: 'salonized-pay'
                  }
                }"
                v-test="'sp-black-friday-offer'"
              />
              <SalonizedPayBanner
                v-if="
                  !showTreatwellOfferBanner &&
                  !(
                    canGetBoost &&
                    unleash.isEnabled('SubscriptionBoost') &&
                    !companySettings.general.boughtTerminalFromSubscriptionFlow
                  )
                "
              />
              <NewFeatureCard
                v-if="
                  !showTreatwellOfferBanner &&
                  unleash.isEnabled('ServiceAvailability') &&
                  !unleash.isEnabled('SalonizedPay')
                "
                featureName="waiting_list_per_day"
              />
              <QuickStart v-if="!singleColumn" />
              <Notes :data="dashboard.notes" />
              <Occupation :data="dashboard.resourcesToday" />
              <CustomersToday />
              <CalendarUtilisation
                :utilizationToday="dashboard.utilizationToday"
                :utilizationSevenDays="dashboard.utilizationSevenDays"
              />
            </template>
          </div>
        </div>
      </div>
    </div>
  </ModuleBase>
</template>

<script lang="ts">
import gql from 'graphql-tag';
import { mapState, storeToRefs } from 'pinia';
import { defineComponent } from 'vue';
import apolloClient from '@/apollo/client';
import dayjs from '@/dayjs';
import filters from '@/filters';
import { brandNamePay } from '@/helpers/brand-name';
import { modal } from '@/helpers/ui';
import BoostBanner from '@/modules/_shared/boost-banner/index.vue';
import ModuleBase from '@/modules/ModuleBase.vue';
import IntentReminderModal from '@/modules/treatwell/modals/IntentReminderModal.vue';
import TwUltimatumModal from '@/modules/treatwell/modals/UltimatumModal.vue';
import { useAdyenStore } from '@/stores/adyen';
import { useCalendarFiltersStore } from '@/stores/calendar-filters';
import { useCompanyStore } from '@/stores/company';
import { useLocationsStore } from '@/stores/locations';
import { useTreatwellStore } from '@/stores/treatwell';
import { useUserStore } from '@/stores/user';
import unleash from '@/unleash';
import CalendarUtilisation from './CalendarUtilisation.vue';
import CustomersToday from './CustomersToday.vue';
import NewFeatureCard from './NewFeatureCard.vue';
import Notes from './Notes.vue';
import Occupation from './Occupation.vue';
import OnlineBookings from './OnlineBookings.vue';
import QuickStart from './QuickStart.vue';
import SalonizedPayBanner from './SalonizedPayBanner.vue';
import SalonizedPayVerification from './SalonizedPayVerification.vue';
import ScheduleActivity from './schedule-activity/index.vue';
import TreatwellDac7Verification from './TreatwellDac7Verification.vue';
import TreatwellMarketplaceBanner from './TreatwellMarketplaceBanner.vue';

export default defineComponent({
  components: {
    ScheduleActivity,
    QuickStart,
    CustomersToday,
    Occupation,
    Notes,
    CalendarUtilisation,
    ModuleBase,
    OnlineBookings,
    IntentReminderModal,
    NewFeatureCard,
    TwUltimatumModal,
    SalonizedPayBanner,
    BoostBanner,
    SalonizedPayVerification,
    TreatwellDac7Verification,
    TreatwellMarketplaceBanner,
  },
  setup() {
    const { canGetBoost } = storeToRefs(useCompanyStore());
    const { companySettings, isUk } = useCompanyStore();
    const { adyenAppState } = useAdyenStore();
    const { adyenAppStateLoaded, hasTerminals } = storeToRefs(useAdyenStore());

    const showSalonizedPayAlert = computed(
      () =>
        companySettings.general.boughtTerminalFromSubscriptionFlow &&
        adyenAppStateLoaded.value &&
        adyenAppState.verificationStatus !== 'VALID',
    );

    return {
      canGetBoost,
      apolloClient,
      unleash,
      showSalonizedPayAlert,
      companySettings,
      hasTerminals,
      isUk,
      brandNamePay,
      filters,
    };
  },
  data() {
    return {
      singleColumn: false,
    };
  },
  apollo: {
    dashboard: {
      query: gql`
        query getDashboard($locationId: ID) {
          dashboard(locationId: $locationId) {
            notes {
              completedAt
              id
              resourceId
              text
            }
            resourcesToday {
              appointments
              available
              occupied
              resourceId
              roster
              sortOrder
              timePresent
            }
            utilizationSevenDays {
              available
              occupied
              timePresent
            }
            utilizationToday {
              available
              occupied
              timePresent
            }
          }
        }
      `,
      pollInterval: 5 * 60 * 1000,
      variables() {
        return {
          locationId: this.locationId,
        };
      },
    },
  },
  computed: {
    ...mapState(useCompanyStore, ['company', 'isTrial']),
    ...mapState(useLocationsStore, ['locationId']),
    ...mapState(useTreatwellStore, ['showTreatwellOfferBanner']),
    ...mapState(useUserStore, ['hasFeatureFlag']),
    isBelgianCompany() {
      return this.company?.country?.toLowerCase() === 'be';
    },
    offerPrice() {
      return this.filters.currency(10000);
    },
    showSplitWalletsBanner() {
      return (
        unleash.isEnabled('SalonizedPay') &&
        unleash.isEnabled('SplitWallets') &&
        this.hasFeatureFlag('admin-salonized-pay')
      );
    },
  },
  methods: {
    checkWidth() {
      this.singleColumn = document.documentElement.clientWidth <= 768;
    },
  },
  beforeRouteLeave(to, _from, next) {
    if (to?.name === 'create-appointment') {
      const store = useCalendarFiltersStore();
      store.date = dayjs.tz().format('YYYY-MM-DD');
    }

    next();
  },
  created() {
    this.checkWidth();
    window.addEventListener('resize', this.checkWidth);

    if (this.$route.query.payment === 'success') {
      modal('welcome');

      this.$router.replace({
        name: 'dashboard',
      });
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkWidth);
  },
});
</script>

<style
  lang="scss"
  module
>
.base {
  max-width: 1000px;
  margin: 0 auto;

  .base.isSmall & {
    padding-top: $spacing;
  }
}

.content {
  .base:not(.singleColumn) & {
    display: flex;
    justify-content: space-between;
  }
}

.main {
  .base.singleColumn & {
    margin-bottom: $spacing;
  }

  .base:not(.singleColumn) & {
    width: calc(61% - #{$spacing * 0.5});
  }
}

.side {
  position: relative;

  .base:not(.singleColumn) & {
    width: calc(39% - #{$spacing * 0.5});
  }
}

.main,
.side {
  & > *:not(:last-child) {
    margin-bottom: $spacing;
  }
}

.splitWalletsBanner {
  margin-bottom: $spacing;
}
</style>
